import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'

export type DropdownButtonProps<T> = {
  title: string
  options: T[]
  dropdownMenuMinWidth?: number

  /**
   * Align the dropdown menu to the left, center or right of the button
   */
  alignMenu?: 'start' | 'center' | 'end'

  renderOption: (item: T, onClose: () => void) => JSX.Element
  onOpen?: () => void
  onClose?: () => void
}

export const DropdownButton = <T,>({
  title,
  options,
  dropdownMenuMinWidth,
  alignMenu = 'center',
  renderOption,
  onOpen,
  onClose,
}: DropdownButtonProps<T>) => {
  const [showMenu, setShowMenu] = useState(false)

  const toggleMenu = () => {
    if (showMenu) {
      onClose?.()
    } else {
      onOpen?.()
    }
    setShowMenu(!showMenu)
  }

  const closeMenu = () => {
    if (showMenu) {
      onClose?.()
      setShowMenu(false)
    }
  }

  return (
    <StDropdownContainer>
      <StButton onClick={toggleMenu} variant={'Primary'}>
        {title}
        <StIcon icon={showMenu ? ['fal', 'chevron-up'] : ['fal', 'chevron-down']} />
      </StButton>
      <StDropdownMenu show={showMenu} align={alignMenu} minWidth={dropdownMenuMinWidth}>
        {options.map((option) => renderOption(option, closeMenu))}
      </StDropdownMenu>
    </StDropdownContainer>
  )
}

const StDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 99;
`

const StButton = styled(Button)`
  color: white;
  border: none;
  cursor: pointer;
`

const StIcon = styled(FontAwesomeIcon)`
  margin-left: ${({ theme }) => theme.UI.Spacing.Space3}px;
`

const StDropdownMenu = styled.div<{
  show: boolean
  align: 'start' | 'center' | 'end'
  minWidth?: number
}>`
  display: ${({ show }) => (show ? 'block' : 'none')};
  min-width: ${({ minWidth }) => minWidth || 250}px;

  position: absolute;
  left: ${({ align }) => (align === 'start' ? '-100%' : align === 'end' ? 'auto' : '50%')};
  margin-top: ${({ theme }) => theme.UI.Spacing.Space3}px;

  background-color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  z-index: 1;
`
